/**
 * On the submit panel: the workflow this document will follow, its route,
 * and the choice of a different one.
 *
 * The default is what routing gives the document (its type and department).
 * The author may pick another workflow; then they say why, and the approvers
 * are shown that. Owner, 2026-09-20: "a document and department has a default
 * workflow and then the user can pick a different workflow if they want
 * something specific or different from the default."
 */

import { useEffect, useState } from 'react';
import { RoutePreview } from './RoutePreview';
import { useRoutePreview } from './approvalsData';
import { SUMMARY_MAX } from '../../types/fieldLimits';
import own from './Approvals.module.css';

/** Mirrors CHOICE_REASON_MIN in functions/src/policies/saveDraft.ts. */
export const CHOICE_REASON_MIN = 10;

export interface RouteChoice {
  /** Set only when a workflow other than the default was picked. */
  workflowId?: string;
  workflowReason?: string;
  /** False while a reason is still owed. */
  ready: boolean;
}

export function SubmitRoute({ policyId, idPrefix, onChange }: { policyId: string; idPrefix: string; onChange: (choice: RouteChoice) => void }) {
  // Asked once for the default and the choices; the preview below asks again for whichever is picked.
  const base = useRoutePreview({ policyId });
  const [picked, setPicked] = useState('');
  const [reason, setReason] = useState('');

  const choices = base.data?.choices ?? [];
  const defaultRoute = base.data?.defaultRoute ?? null;
  const owed = picked !== '' && reason.trim().length < CHOICE_REASON_MIN;

  useEffect(() => {
    onChange(picked ? { workflowId: picked, workflowReason: reason, ready: !owed } : { ready: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, reason, owed]);

  return (
    <div>
      {choices.length > 0 && (
        <div className={own.commentField} style={{ marginTop: 'var(--sp-4)' }}>
          <label className={own.label} htmlFor={`${idPrefix}-workflow`}>Approval workflow</label>
          <select id={`${idPrefix}-workflow`} className={own.select} value={picked} onChange={(e) => setPicked(e.target.value)}>
            <option value="">{defaultRoute ? `${defaultRoute.workflowName} (the default for this document)` : 'The default for this document'}</option>
            {choices.map((c) => (
              <option key={c.workflowId} value={c.workflowId}>{c.name} ({c.steps.length === 1 ? '1 step' : `${c.steps.length} steps`})</option>
            ))}
          </select>
        </div>
      )}

      <RoutePreview ask={picked ? { policyId, workflowId: picked } : { policyId }} heading="Who will approve this" />

      {picked !== '' && (
        <div className={own.commentField}>
          <label className={own.label} htmlFor={`${idPrefix}-why`}>
            Why this workflow and not the default <span className={own.required}>(required)</span>
          </label>
          <textarea
            id={`${idPrefix}-why`} className={own.textarea} rows={2} maxLength={SUMMARY_MAX} value={reason} required
            aria-describedby={`${idPrefix}-why-hint`} onChange={(e) => setReason(e.target.value)}
          />
          <p className={own.itemMeta} id={`${idPrefix}-why-hint`}>
            The approvers see this, and it is kept on the record. At least {CHOICE_REASON_MIN} characters.
          </p>
        </div>
      )}
    </div>
  );
}
